import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, ButtonBase } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import packingService from '../../services/packingService';
import { PACKING_MODES, PackingMode, PackingModeId } from './packingModes';

interface PackingHomeProps {
  personnelName: string;
  warehouseId: string;
  onSelectMode: (mode: PackingModeId) => void;
}

const MODE_ICONS: Record<PackingMode['icon'], React.ReactNode> = {
  cart: <ShoppingCartIcon />,
  box: <Inventory2Icon />,
  shelf: <ViewModuleIcon />,
};

interface ModeCardProps {
  icon: React.ReactNode;
  title: string;
  desc: string;
  count: number;
  color: string;
  onClick: () => void;
}

const ModeCard: React.FC<ModeCardProps> = ({ icon, title, desc, count, color, onClick }) => (
  <ButtonBase
    onClick={onClick}
    focusRipple
    sx={{
      width: '100%',
      textAlign: 'left',
      borderRadius: 4,
      p: { xs: 2.5, sm: 3 },
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 2, sm: 3 },
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      '&:hover': {
        borderColor: color,
        boxShadow: `0 8px 24px ${color}26`,
        transform: 'translateY(-2px)',
      },
      '&:active': { transform: 'translateY(0)' },
    }}
  >
    <Box
      sx={{
        width: { xs: 60, sm: 72 },
        height: { xs: 60, sm: 72 },
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        bgcolor: `${color}14`,
        color,
        '& svg': { fontSize: { xs: 32, sm: 38 } },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', sm: '1.7rem' }, lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.25, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
        {desc}
      </Typography>
    </Box>
    <Typography sx={{ color, fontWeight: 800, fontSize: { xs: '2rem', sm: '2.4rem' }, lineHeight: 1, minWidth: 48, textAlign: 'right' }}>
      {count}
    </Typography>
    <ChevronRightIcon sx={{ color, fontSize: { xs: 28, sm: 32 }, flexShrink: 0 }} />
  </ButtonBase>
);

const PackingHome: React.FC<PackingHomeProps> = ({ personnelName, warehouseId, onSelectMode }) => {
  const { t } = useTranslation('warehouse');
  const [counts, setCounts] = useState<Record<PackingModeId, number>>({ cart: 0, product: 0, slot: 0 });

  const loadCounts = useCallback(async () => {
    if (!warehouseId) return;
    try {
      const orders = await packingService.getPending(warehouseId);
      const singleCount = orders.filter((o) => o.orderType === 'Single').length;
      const multiCount = orders.filter((o) => o.orderType === 'Multi').length;
      setCounts({ cart: orders.length, product: singleCount, slot: multiCount });
    } catch { /* ignore */ }
  }, [warehouseId]);

  useEffect(() => {
    loadCounts();
    const interval = setInterval(loadCounts, 5000);
    return () => clearInterval(interval);
  }, [loadCounts]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 2, sm: 4 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 600 }}>
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', sm: '2.4rem' }, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
            {t('screens.packing.home.greeting')}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', sm: '2.4rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', color: 'primary.main' }}>
            {personnelName}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1.5, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
            {t('screens.packing.home.prompt')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
          {PACKING_MODES.map((mode) => (
            <ModeCard
              key={mode.id}
              icon={MODE_ICONS[mode.icon]}
              title={t(`screens.packing.modes.${mode.i18nKey}.title`)}
              desc={t(`screens.packing.modes.${mode.i18nKey}.desc`)}
              count={counts[mode.id]}
              color={mode.color}
              onClick={() => onSelectMode(mode.id)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PackingHome;
